import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from './firebase';
import type { School, SchoolPrefs, AppUser, Cycle, Review, ReviewLmDraft, ReviewProgress, SchoolDocument, CpdEntry, AppNotification, ObjectiveTemplate, AuditEntry, LeaveRequest, LeaveConfig, CoverEntry, Policy, PolicyAck, RosterStudent, RosterStaff, RosterRoom, RosterClass, BehaviourEvent, BehaviourConfig, Detention, OnCallAlert, Isolation, OutOfLesson, PastoralConfig, OptionsConfig, OptionChoice, Survey, SurveyResponse, Feedback, WellbeingConfig, PupilWellbeingResponse, CalendarConfig, SendConfig, SendRegisterEntry, SendPassport, SendProvision, SendProvisionLog, StaffAbsenceConfig, SendReview, SendContributionRequest, SendContribution, SendOutcome, SendComplianceSnapshot, TermDates, PendingLineManager, MisBehaviourRecord } from '../types';

export const schoolsCol    = collection(db, 'schools')     as CollectionReference<School>;
export const cpdEntriesCol = collection(db, 'cpdEntries') as CollectionReference<CpdEntry>;
export const usersCol = collection(db, 'users') as CollectionReference<AppUser>;
export const cyclesCol = collection(db, 'cycles') as CollectionReference<Cycle>;
export const reviewsCol = collection(db, 'reviews') as CollectionReference<Review>;
export const documentsCol = collection(db, 'documents') as CollectionReference<SchoolDocument>;

export const schoolDoc = (id: string) =>
  doc(db, 'schools', id) as DocumentReference<School>;

export const schoolPrefsDoc = (schoolId: string) =>
  doc(db, 'schoolPrefs', schoolId) as DocumentReference<SchoolPrefs>;

export const userDoc = (id: string) =>
  doc(db, 'users', id) as DocumentReference<AppUser>;

export const cycleDoc = (id: string) =>
  doc(db, 'cycles', id) as DocumentReference<Cycle>;

export const reviewDoc = (id: string) =>
  doc(db, 'reviews', id) as DocumentReference<Review>;

/** The line manager's unconfirmed work on a review. Same id as the review. */
export const reviewLmDraftDoc = (reviewId: string) =>
  doc(db, 'reviewLmDrafts', reviewId) as DocumentReference<ReviewLmDraft>;

/** Where a review has got to — status only, readable before it's submitted. */
export const reviewProgressCol = collection(db, 'reviewProgress') as CollectionReference<ReviewProgress>;
export const reviewProgressDoc = (reviewId: string) =>
  doc(db, 'reviewProgress', reviewId) as DocumentReference<ReviewProgress>;

/** The statuses at which a line manager may read a review (firestore.rules).
 *  Every reviewer query on `reviews` must constrain status to these, or the
 *  rules deny it. */
export const REVIEWER_VISIBLE_STATUSES = [
  'submittedByStaff',
  'confirmedByLineManager',
  'midYearOpen',
  'midYearComplete',
] as const;

export const documentDoc = (id: string) =>
  doc(db, 'documents', id) as DocumentReference<SchoolDocument>;

export const cpdEntryDoc = (id: string) =>
  doc(db, 'cpdEntries', id) as DocumentReference<CpdEntry>;

export const notificationsCol = collection(db, 'notifications') as CollectionReference<AppNotification>;

export const objectiveTemplatesCol = collection(db, 'objectiveTemplates') as CollectionReference<ObjectiveTemplate>;

export const auditLogCol = collection(db, 'auditLog') as CollectionReference<AuditEntry>;

// Leave & Cover module.
// leaveRequests holds the reason and the evidence pointer — its read rule is the
// privacy boundary. coverEntries is the redacted projection the cover team reads;
// its doc id matches the request it came from.
export const leaveRequestsCol = collection(db, 'leaveRequests') as CollectionReference<LeaveRequest>;
export const coverEntriesCol  = collection(db, 'coverEntries')  as CollectionReference<CoverEntry>;
export const leaveRequestDoc = (id: string) =>
  doc(db, 'leaveRequests', id) as DocumentReference<LeaveRequest>;
export const leaveConfigDoc = (schoolId: string) =>
  doc(db, 'leaveConfig', schoolId) as DocumentReference<LeaveConfig>;

export const policiesCol = collection(db, 'policies') as CollectionReference<Policy>;

export const policyDoc = (id: string) =>
  doc(db, 'policies', id) as DocumentReference<Policy>;

export const policyAcksCol = collection(db, 'policyAcks') as CollectionReference<PolicyAck>;

// Wellbeing module
export const wellbeingConfigDoc = (schoolId: string) =>
  doc(db, 'wellbeingConfig', schoolId) as DocumentReference<WellbeingConfig>;
export const pupilWellbeingCol = collection(db, 'pupilWellbeingResponses') as CollectionReference<PupilWellbeingResponse>;

// Term dates and INSET days — school-wide, set from School Settings. Read it
// through useSchool().terms, which falls back to pastoralConfig.terms.
export const termDatesDoc = (schoolId: string) =>
  doc(db, 'termDates', schoolId) as DocumentReference<TermDates>;

// Calendar subscription (platform feature, not a module) — one config doc per school
export const calendarConfigDoc = (schoolId: string) =>
  doc(db, 'calendarConfig', schoolId) as DocumentReference<CalendarConfig>;

// ParkerMarker: per-school default comment-bank headings (Feedback Slips).
export interface FeedbackConfig {
  schoolId: string;
  defaultSlot1Heading?: string | null;
  defaultSlot2Heading?: string | null;
  defaultSlot3Heading?: string | null;
  updatedAt?: unknown;
}
export const feedbackConfigDoc = (schoolId: string) =>
  doc(db, 'feedbackConfig', schoolId) as DocumentReference<FeedbackConfig>;

// Wonde-synced canonical roster, read-only for school members
export const rosterStudentsCol = (schoolId: string) =>
  collection(db, 'schools', schoolId, 'students') as CollectionReference<RosterStudent>;

// MIS behaviour + achievement records, mirrored hourly; read-only to the app
export const misBehaviourCol = (schoolId: string) =>
  collection(db, 'schools', schoolId, 'misBehaviour') as CollectionReference<MisBehaviourRecord>;

export const rosterStaffCol = (schoolId: string) =>
  collection(db, 'schools', schoolId, 'staffDirectory') as CollectionReference<RosterStaff>;

export const pendingLineManagersCol = collection(db, 'pendingLineManagers') as CollectionReference<PendingLineManager>;

export const rosterRoomsCol = (schoolId: string) =>
  collection(db, 'schools', schoolId, 'rosterRooms') as CollectionReference<RosterRoom>;

export const rosterClassesCol = (schoolId: string) =>
  collection(db, 'schools', schoolId, 'rosterClasses') as CollectionReference<RosterClass>;

// Behaviour module (top-level, school-scoped by field; staff-only via rules)
export const behaviourConfigDoc = (schoolId: string) =>
  doc(db, 'behaviourConfig', schoolId) as DocumentReference<BehaviourConfig>;
export const behaviourEventsCol = collection(db, 'behaviourEvents') as CollectionReference<BehaviourEvent>;
export const detentionsCol      = collection(db, 'detentions')      as CollectionReference<Detention>;
export const onCallAlertsCol    = collection(db, 'onCallAlerts')    as CollectionReference<OnCallAlert>;
export const isolationsCol      = collection(db, 'isolations')      as CollectionReference<Isolation>;
export const outOfLessonCol     = collection(db, 'outOfLesson')     as CollectionReference<OutOfLesson>;
export const pastoralConfigDoc  = (schoolId: string) =>
  doc(db, 'pastoralConfig', schoolId) as DocumentReference<PastoralConfig>;

// Voice (surveys + feedback) — part of the wellbeing module
export const surveysCol         = collection(db, 'surveys')         as CollectionReference<Survey>;
export const surveyResponsesCol = collection(db, 'surveyResponses') as CollectionReference<SurveyResponse>;
export const feedbackCol        = collection(db, 'feedback')        as CollectionReference<Feedback>;

// KS4 Options module (one config doc per school; one choice doc per pupil)
export const optionsConfigDoc = (schoolId: string) =>
  doc(db, 'optionsConfig', schoolId) as DocumentReference<OptionsConfig>;
export const optionChoicesCol = collection(db, 'optionChoices') as CollectionReference<OptionChoice>;
export const optionChoiceDoc = (schoolId: string, studentId: string) =>
  doc(db, 'optionChoices', `${schoolId}__${studentId}`) as DocumentReference<OptionChoice>;


// ─────────────────────────────────────────────────────────────────────────────
// SEND & EHCP module.
//
// Only the two Tier 0/1 collections have client accessors at all. sendCases,
// sendCaseNotes, sendDocuments and sendAccessLog are closed in rules and read
// exclusively through callables — deliberately, because a direct client read
// cannot be audited, and "who viewed which record" is a requirement for this
// data. Adding an accessor for them here would be the first step to breaking that.
// ─────────────────────────────────────────────────────────────────────────────

export const sendRegisterCol = collection(db, 'sendRegister') as CollectionReference<SendRegisterEntry>;

export const sendConfigDoc = (schoolId: string) =>
  doc(db, 'sendConfig', schoolId) as DocumentReference<SendConfig>;

// Staff Absence. Only the config is addressable: `staffAbsences` is closed to
// clients and read through getBradfordReport / getStaffAbsenceCalendar, so the
// viewer check happens on the server on every request.
export const staffAbsenceConfigDoc = (schoolId: string) =>
  doc(db, 'staffAbsenceConfig', schoolId) as DocumentReference<StaffAbsenceConfig>;

/** The register is one document per pupil, addressed without a query. */
export const sendRegisterDoc = (schoolId: string, studentId: string) =>
  doc(db, 'sendRegister', `${schoolId}__${studentId}`) as DocumentReference<SendRegisterEntry>;

/**
 * Tier 0. A teacher's listener MUST constrain both schoolId and
 * visibleStaffUids (array-contains) — the rule tests both, and a listen is only
 * authorised when the query's own constraints prove every match readable.
 * That also means array-contains is spent: filter by profile in memory.
 */
export const sendPassportsCol = collection(db, 'sendPassports') as CollectionReference<SendPassport>;
export const sendPassportDoc = (schoolId: string, studentId: string) =>
  doc(db, 'sendPassports', `${schoolId}__${studentId}`) as DocumentReference<SendPassport>;

/**
 * Section F. A deliverer's listener on sendProvisions must constrain schoolId
 * AND deliveredByUid; the compliance tier may list the school. Logs are the
 * one client-WRITTEN SEND collection — addDoc here, with loggedByUid pinned to
 * the caller by the rules and the provision's clock moved by a trigger.
 */
export const sendProvisionsCol = collection(db, 'sendProvisions') as CollectionReference<SendProvision>;
export const sendProvisionLogsCol = collection(db, 'sendProvisionLogs') as CollectionReference<SendProvisionLog>;

/**
 * The annual review. sendReviews is compliance-tier only. A contributor's
 * listener on sendContributionRequests must constrain schoolId AND
 * assigneeUid. Their answer is a client setDoc at the deterministic id.
 */
export const sendReviewsCol = collection(db, 'sendReviews') as CollectionReference<SendReview>;
export const sendContributionRequestsCol = collection(db, 'sendContributionRequests') as CollectionReference<SendContributionRequest>;
export const sendContributionsCol = collection(db, 'sendContributions') as CollectionReference<SendContribution>;
export const sendContributionDoc = (schoolId: string, reviewId: string, uid: string) =>
  doc(db, 'sendContributions', `${schoolId}__${reviewId}__${uid}`) as DocumentReference<SendContribution>;

/** Section E outcomes (compliance tier) and the sweep's weekly snapshot. */
export const sendOutcomesCol = collection(db, 'sendOutcomes') as CollectionReference<SendOutcome>;
export const sendComplianceSnapshotsCol = collection(db, 'sendComplianceSnapshots') as CollectionReference<SendComplianceSnapshot>;
